/* eslint-disable max-lines */
import { QuestionnaireData } from "@teamprevent/common/questionnaires/model";
import { TaskModel } from "@teamprevent/common/task/taskActions/models";
export {
    OriginTypes, CurrentStatus, Level, levelOptions, ToyotaClinicalNotesLevel, toyotaClinicalNotesLevelOptions,
    currentStatusOptions, injuredQuestionOptions, originTypeOptions, Symptoms, symptomsOptions, ohaMedicalClassificationOptions,
    medicalClassificationSubCategoryOptions
} from "@teamprevent/common/referralReport/models";

export const ClinicalNotesVersion = {
    /**
     * First version of clinical notes, with common core and added case specific sections.
     */
    V1: 1,

    /**
     * Second version of clinical notes, referral clinical notes specific change - v1 is no longer used for new referral clinical notes.
     */
    V2: 2,

    /**
     * Third version of clinical notes, created to support new version of Physiotherapy clinical notes.
     */
    V3: 3,

    /**
     * Fourth version of clinical notes, created to support various changes to layouts and structure of clinical notes.
     */
    Latest: 4
} as const;

export const ohpTaskTypes = [
    "InitialTelephoneOhpReferral",
    "InitialFaceToFaceOhpReferral",
    "InitialVideoOhpReferral",
    "FollowUpTelephoneOhpReferral",
    "FollowUpFaceToFaceOhpReferral",
    "FollowUpVideoOhpReferral"
];

export interface DidNotAttendResponse {
    ohCaseId: string;
    appointmentTasks: TaskModel[];
}
export enum ConsultationMethodOption {
    Telephone = "Telephone",
    Video = "Video",
    FaceToFace = "FaceToFace"
}

export enum AccidentOutcome {
    EscalatetoHealthConcernSystem = "EscalatetoHealthConcernSystem",
    ReturntoWorkDischarge = "ReturntoWorkDischarge",
    SentHomeHospitalOwnGP = "SentHomeHospitalOwnGP"
}

export enum TypeOfInjury {
    AcutePain = "AcutePain",
    Abrasion = "Abrasion",
    Bruising = "Bruising",
    Impact = "Impact",
    Sprain = "Sprain",
    EyeInjury = "EyeInjury",
    Laceration = "Laceration",
    Fracture = "Fracture",
    Swelling = "Swelling",
    Electrocution = "Electrocution",
    Strain = "Strain",
    Puncture = "Puncture",
    Crush = "Crush",
    Burns = "Burns"
}

export const accidentOutcomeOptions = [
    { text: "Escalate to Health Concern System", value: AccidentOutcome.EscalatetoHealthConcernSystem },
    { text: "Return to Work Discharge", value: AccidentOutcome.ReturntoWorkDischarge },
    { text: "Sent Home / Hospital / Own GP", value: AccidentOutcome.SentHomeHospitalOwnGP }
];

export const typeOfInjuryOptions = [
    { text: "Acute Pain", value: TypeOfInjury.AcutePain },
    { text: "Abrasion", value: TypeOfInjury.Abrasion },
    { text: "Bruising", value: TypeOfInjury.Bruising },
    { text: "Impact", value: TypeOfInjury.Impact },
    { text: "Sprain", value: TypeOfInjury.Sprain },
    { text: "Eye Injury", value: TypeOfInjury.EyeInjury },
    { text: "Laceration", value: TypeOfInjury.Laceration },
    { text: "Fracture", value: TypeOfInjury.Fracture },
    { text: "Swelling", value: TypeOfInjury.Swelling },
    { text: "Electrocution", value: TypeOfInjury.Electrocution },
    { text: "Strain", value: TypeOfInjury.Strain },
    { text: "Puncture", value: TypeOfInjury.Puncture },
    { text: "Crush", value: TypeOfInjury.Crush },
    { text: "Burns", value: TypeOfInjury.Burns }
];

export const consultationMethodOptions = [
    { text: "Telephone", value: ConsultationMethodOption.Telephone },
    { text: "Video", value: ConsultationMethodOption.Video },
    { text: "Face to Face", value: ConsultationMethodOption.FaceToFace }
];

export enum CurrentWorkStatus {
    InWorkFullDuties = "InWorkFullDuties",
    InWOrkRestrictions = "InWOrkRestrictions",
    OffWork = "OffWork",
    Suspended = "Suspended",
    OnPhasedReturn = "OnPhasedReturn"
}

export const currentWorkStatusOptions = [
    { text: "In Work (Full Duties)", value: CurrentWorkStatus.InWorkFullDuties },
    { text: "In Work (Restrictions)", value: CurrentWorkStatus.InWOrkRestrictions },
    { text: "Off Work", value: CurrentWorkStatus.OffWork },
    { text: "Suspended", value: CurrentWorkStatus.Suspended },
    { text: "On Phased Return", value: CurrentWorkStatus.OnPhasedReturn }
];

export enum FunctionalAssessmentTestType {
    Dropdown = "Dropdown",
    TextBox = "TextBox",
}

export const dropdownFunctionalAssessmentTestOptions = [
    { text: "Up to 5KG", value: "UpTo5KG" },
    { text: "10KG", value: "10KG" },
    { text: "Up to 15KG", value: "UpTo15KG" },
    { text: "Up to 23KG", value: "UpTo23KG" }
];

export const functionalAssessmentTestOptions = [
    { text: "Lift/Carry (Waist Height)", value: "FunctionAssessmentTestLiftCarryWaistHeight", type: FunctionalAssessmentTestType.Dropdown },
    { text: "Lift/Carry (Shoulder height)", value: "FunctionAssessmentTestLiftCarryShoulderHeight", type: FunctionalAssessmentTestType.Dropdown },
    { text: "Push Pull", value: "FunctionAssessmentTestPushPull", type: FunctionalAssessmentTestType.Dropdown },
    { text: "Grip strength (L, R, B)", value: "FunctionAssessmentTestGripStrengthLRB", type: FunctionalAssessmentTestType.TextBox },
    { text: "At shoulder reach: (L, R, B)", value: "FunctionAssessmentTestAtShoulderReachLRB", type: FunctionalAssessmentTestType.TextBox },
    { text: "Overhead reach: (L, R, B)", value: "FunctionAssessmentTestOverheadReachLRB", type: FunctionalAssessmentTestType.TextBox },
    { text: "Head/neck rotation (>20º): (L, R, B)", value: "FunctionAssessmentTestHeadNeckRotation20LRB", type: FunctionalAssessmentTestType.TextBox },
    { text: "Spinal range of movement", value: "FunctionAssessmentTestSpinalRangeOfMovement", type: FunctionalAssessmentTestType.TextBox },
    { text: "Kneel (L, R, B)", value: "FunctionAssessmentTestKneelLRB", type: FunctionalAssessmentTestType.TextBox },
    { text: "Squat", value: "FunctionAssessmentTestSquat", type: FunctionalAssessmentTestType.TextBox },
    { text: "Sitting", value: "FunctionAssessmentTestSitting", type: FunctionalAssessmentTestType.TextBox },
    { text: "Standing", value: "FunctionAssessmentTestStanding", type: FunctionalAssessmentTestType.TextBox },
    { text: "Walking", value: "FunctionAssessmentTestWalking", type: FunctionalAssessmentTestType.TextBox },
    { text: "Step Up e.g. stairs/step stool (L, R, B)", value: "FunctionAssessmentTestStepUpStairsStepStoolLRB", type: FunctionalAssessmentTestType.TextBox },
    { text: "Crawl", value: "FunctionAssessmentTestCrawl", type: FunctionalAssessmentTestType.TextBox },
    { text: "See", value: "FunctionAssessmentTestSee", type: FunctionalAssessmentTestType.TextBox },
    { text: "Hear", value: "FunctionAssessmentTestHear", type: FunctionalAssessmentTestType.TextBox },
    { text: "Talk", value: "FunctionAssessmentTestTalk", type: FunctionalAssessmentTestType.TextBox },
    { text: "Driving commercial machinery", value: "FunctionAssessmentTestDrivingCommercialMachinery", type: FunctionalAssessmentTestType.TextBox },
];

export enum Diabetic {
    No = "No",
    Type1 = "Type 1",
    Type2 = "Type 2"
}

export enum SmokingStatus {
    No = "No",
    Yes = "Yes",
    ExSmoker = "ExSmoker"
}

export const smokingStatusQuestionOptions = [
    { text: "Yes", value: SmokingStatus.Yes },
    { text: "No", value: SmokingStatus.No },
    { text: "Ex smoker", value: SmokingStatus.ExSmoker }
];

export const diabeticOptions = [
    { text: "No", value: Diabetic.No },
    { text: "Type 1", value: Diabetic.Type1 },
    { text: "Type 2", value: Diabetic.Type2 }
];

export enum PsychologicalAssessmentOutcome {
    NotScored = "NotScored",
    NoDepression = "NoDepression",
    MildDepression = "MildDepression",
    ModerateDepression = "ModerateDepression",
    ModeratelySevereDepression = "ModeratelySevereDepression",
    SevereDepression = "SevereDepression",
    Normal = "Normal",
    NoAnxiety = "NoAnxiety",
    MildAnxiety = "MildAnxiety",
    ModerateAnxiety = "ModerateAnxiety",
    SevereAnxiety = "SevereAnxiety",
    NoFunctionalImpairment = "NoFunctionalImpairment",
    SignificantFunctionalImpairment = "SignificantFunctionalImpairment",
    ModerateOrSevereFunctionalImpairment = "ModerateOrSevereFunctionalImpairment",
    MildToModerateFunctionalImpairment = "MildToModerateFunctionalImpairment"
}

export const psychologicalAssessmentOutcomeQuestionOptions = [
    { text: "Not scored", value: PsychologicalAssessmentOutcome.NotScored },
    { text: "No depression", value: PsychologicalAssessmentOutcome.NoDepression },
    { text: "Mild depression", value: PsychologicalAssessmentOutcome.MildDepression },
    { text: "Moderate depression", value: PsychologicalAssessmentOutcome.ModerateDepression },
    { text: "Moderately severe depression", value: PsychologicalAssessmentOutcome.ModeratelySevereDepression },
    { text: "Severe depression", value: PsychologicalAssessmentOutcome.SevereDepression },
    { text: "Normal", value: PsychologicalAssessmentOutcome.Normal },
    { text: "No Anxiety", value: PsychologicalAssessmentOutcome.NoAnxiety },
    { text: "Mild anxiety", value: PsychologicalAssessmentOutcome.MildAnxiety },
    { text: "Moderate anxiety", value: PsychologicalAssessmentOutcome.ModerateAnxiety },
    { text: "Severe anxiety", value: PsychologicalAssessmentOutcome.SevereAnxiety },
    { text: "No functional impairment", value: PsychologicalAssessmentOutcome.NoFunctionalImpairment },
    { text: "Significant functional impairment", value: PsychologicalAssessmentOutcome.SignificantFunctionalImpairment },
    { text: "Moderate to severe functional impairment", value: PsychologicalAssessmentOutcome.ModerateOrSevereFunctionalImpairment },
    { text: "Mild to moderate functional impairment", value: PsychologicalAssessmentOutcome.MildToModerateFunctionalImpairment }
];

export enum ImpactOnAbilityToPerformJob {
    Demand = "Demand",
    Control = "Control",
    Support = "Support",
    Role = "Role",
    Relationships = "Relationships",
    Change = "Change",
    CognitionAndMemory = "CognitionAndMemory"
}

export const impactOnAbilityToPerformJobQuestionOptions = [
    { text: "Demand", value: ImpactOnAbilityToPerformJob.Demand },
    { text: "Control", value: ImpactOnAbilityToPerformJob.Control },
    { text: "Support", value: ImpactOnAbilityToPerformJob.Support },
    { text: "Role", value: ImpactOnAbilityToPerformJob.Role },
    { text: "Relationships", value: ImpactOnAbilityToPerformJob.Relationships },
    { text: "Change", value: ImpactOnAbilityToPerformJob.Change },
    { text: "Cognition and Memory", value: ImpactOnAbilityToPerformJob.CognitionAndMemory }
];

export enum AdherenceToHomeExerciseAndAdvicePlan {
    Yes = "Yes",
    No = "No",
    NotApplicable = "NotApplicable"
}

export const adherenceToHomeExerciseAndAdvicePlanQuestionOptions = [
    { text: "Yes", value: AdherenceToHomeExerciseAndAdvicePlan.Yes },
    { text: "No", value: AdherenceToHomeExerciseAndAdvicePlan.No },
    { text: "Not applicable", value: AdherenceToHomeExerciseAndAdvicePlan.NotApplicable }
];

export enum Classification {
    AtWorkMedicalCondition = "AtWorkMedicalCondition",
    AtWorkManagementIssue = "AtWorkManagementIssue",
    NotAtWorkMedicalCondition = "NotAtWorkMedicalCondition",
    NotAtWorkManagementIssue = "NotAtWorkManagementIssue"
}

export const classificationQuestionOptions = [
    { text: "At Work: Medical Condition", value: Classification.AtWorkMedicalCondition },
    { text: "At Work: Management Issue", value: Classification.AtWorkManagementIssue },
    { text: "Not at Work: Medical Condition", value: Classification.NotAtWorkMedicalCondition },
    { text: "Not at Work: Management Issue", value: Classification.NotAtWorkManagementIssue }
];

export enum DurationOfMedicalCondition {
    LessThanTwelveMonthsAndUnlikelyToLastLonger = "LessThanTwelveMonthsAndUnlikelyToLastLonger",
    LessThanTwelveMonthsButLikelyToLastAtLeastTwelveMonths = "LessThanTwelveMonthsButLikelyToLastAtLeastTwelveMonths",
    TwelveMonthsOrLonger = "TwelveMonthsOrLonger"
}

export const durationOfMedicalConditionQuestionOptions = [
    { text: "Less than 12 months and unlikely to last longer", value: DurationOfMedicalCondition.LessThanTwelveMonthsAndUnlikelyToLastLonger },
    {
        text: "Less than 12 months but likely to last at least 12 months",
        value: DurationOfMedicalCondition.LessThanTwelveMonthsButLikelyToLastAtLeastTwelveMonths
    },
    { text: "12 months or longer", value: DurationOfMedicalCondition.TwelveMonthsOrLonger }
];

export enum ApplyEqualityAct {
    Yes = "Yes",
    No = "No",
    NotAnswered = "NotAnswered"
}

export const applyEqualityActQuestionOptions = [
    { text: "Yes", value: ApplyEqualityAct.Yes },
    { text: "No", value: ApplyEqualityAct.No },
    { text: "Not Answered", value: ApplyEqualityAct.NotAnswered }
];

export enum FitnessToWork {
    FitForWork = "FitForWork",
    FitWithAdjustments = "FitWithAdjustments",
    TemporarilyUnfitForWork = "TemporarilyUnfitForWork",
    UnfitForWork = "UnfitForWork"
}

export const fitnessToWorkQuestionOptions = [
    { text: "Fit for Work", value: FitnessToWork.FitForWork },
    { text: "Fit with Adjustments", value: FitnessToWork.FitWithAdjustments },
    { text: "Temporarily Unfit for Work", value: FitnessToWork.TemporarilyUnfitForWork },
    { text: "Unfit for Work", value: FitnessToWork.UnfitForWork }
];

export enum DurationOfPhasedReturn {
    OneWeek = "OneWeek",
    TwoWeeks = "TwoWeeks",
    ThreeWeeks = "ThreeWeeks",
    FourWeeks = "FourWeeks",
    FourWeeksPlus = "FourWeeksPlus",    // V4 onwards
    SixWeeksPlus = "SixWeeksPlus"       // Legacy V2/V3
}

export const durationOfPhasedReturnQuestionOptionsLegacy = [
    { text: "1 Week", value: DurationOfPhasedReturn.OneWeek },
    { text: "2 Weeks", value: DurationOfPhasedReturn.TwoWeeks },
    { text: "3 Weeks", value: DurationOfPhasedReturn.ThreeWeeks },
    { text: "4 Weeks", value: DurationOfPhasedReturn.FourWeeks },
    { text: "4 Weeks +", value: DurationOfPhasedReturn.SixWeeksPlus } // Label changed, but value kept for backward compatibility
];

export const durationOfPhasedReturnQuestionOptions = [
    { text: "1 Week", value: DurationOfPhasedReturn.OneWeek },
    { text: "2 Weeks", value: DurationOfPhasedReturn.TwoWeeks },
    { text: "3 Weeks", value: DurationOfPhasedReturn.ThreeWeeks },
    { text: "4 Weeks", value: DurationOfPhasedReturn.FourWeeks },
    { text: "4 Weeks +", value: DurationOfPhasedReturn.FourWeeksPlus },
];

export enum DetailsOfOnwardReferral {
    None = "None",
    Physiotherapy = "Physiotherapy",
    Counselling = "Counselling",
    SelfHelpSignPosting = "SelfHelpSignPosting",
    CBT = "CBT",
    OtherDetailsBelow = "OtherDetailsBelow"
}

export const detailsOfOnwardReferralQuestionOptions = [
    { text: "None", value: DetailsOfOnwardReferral.None },
    { text: "Physiotherapy", value: DetailsOfOnwardReferral.Physiotherapy },
    { text: "Counselling", value: DetailsOfOnwardReferral.Counselling },
    { text: "Self Help / Sign Posting", value: DetailsOfOnwardReferral.SelfHelpSignPosting },
    { text: "CBT ", value: DetailsOfOnwardReferral.CBT },
    { text: "Other - details below", value: DetailsOfOnwardReferral.OtherDetailsBelow },
];

export enum DetailsOfFurtherAppointments {
    NoReview = "NoReview",
    OHAReview = "OHAReview",
    PhysiotherapyReview = "PhysiotherapyReview",
    MentalHealthPractitionerReview = "MentalHealthPractitionerReview",
    OHPhysicianReview = "OHPhysicianReview",
    ReferToGPSpecialist = "ReferToGPSpecialist"
}

export const detailsOfFurtherAppointmentsQuestionOptions = [
    { text: "No Review", value: DetailsOfFurtherAppointments.NoReview },
    { text: "OHA Review", value: DetailsOfFurtherAppointments.OHAReview },
    { text: "Physiotherapy Review", value: DetailsOfFurtherAppointments.PhysiotherapyReview },
    { text: "Mental Health Practitioner Review", value: DetailsOfFurtherAppointments.MentalHealthPractitionerReview },
    { text: "OH Physician Review", value: DetailsOfFurtherAppointments.OHPhysicianReview },
    { text: "Refer to GP / Specialist", value: DetailsOfFurtherAppointments.ReferToGPSpecialist },
];

export enum RiskAssessmentRecommendation {
    None = "None",
    ManualHandling = "ManualHandling",
    DSE = "DSE",
    StressRiskAssessment = "StressRiskAssessment",
    LoneWorking = "LoneWorking",
    NewAndExpectantMothers = "NewAndExpectantMothers",
    OtherPleaseProvideDetails = "OtherPleaseProvideDetails"
}

export const riskAssessmentRecommendationQuestionOptions = [
    { text: "None", value: RiskAssessmentRecommendation.None },
    { text: "Manual Handling", value: RiskAssessmentRecommendation.ManualHandling },
    { text: "DSE", value: RiskAssessmentRecommendation.DSE },
    { text: "Stress Risk Assessment", value: RiskAssessmentRecommendation.StressRiskAssessment },
    { text: "Lone Working", value: RiskAssessmentRecommendation.LoneWorking },
    { text: "New and Expectant Mothers", value: RiskAssessmentRecommendation.NewAndExpectantMothers },
    { text: "Other - Please provide details", value: RiskAssessmentRecommendation.OtherPleaseProvideDetails },
];

export enum WorkRelatedHealthIssue {
    Yes = "Yes",
    No = "No",
    NotKnown = "NotKnown"
}

export const workRelatedHealthIssueQuestionOptions = [
    { text: "Yes", value: WorkRelatedHealthIssue.Yes },
    { text: "No", value: WorkRelatedHealthIssue.No },
    { text: "Not Known", value: WorkRelatedHealthIssue.NotKnown },
];

export const physioMedicalClassificationOptions = [
    { text: "Anxiety", value: "Anxiety", old: true },
    { text: "Assault at Work - MSD", value: "AssaultAtWork" },
    { text: "Autoimmune Disorder", value: "Autoimmune", old: true },
    { text: "Bereavement", value: "Bereavement", old: true },
    { text: "Cancer", value: "Cancer", old: true },
    { text: "Cardiovascular", value: "Cardiovascular", old: true },
    { text: "COVID-19", value: "COVID-19", old: true },
    { text: "Depression", value: "Depression", old: true },
    { text: "Ear Nose and Throat - ENT", value: "EarNoseAndThroat", old: true },
    { text: "Endocrine", value: "Endocrine", old: true },
    { text: "Gastrointestinal", value: "Gastrointestinal", old: true },
    { text: "Genitourinary", value: "Genitourinary", old: true },
    { text: "Gynaecological ", value: "Gynaecological", old: true },
    { text: "Headache/Migraine", value: "HeadacheMigraine", old: true },
    { text: "Infection", value: "Infection", old: true },
    { text: "Injury/Fracture", value: "InjuryFracture", old: true },
    { text: "Liver Disease", value: "LiverDisease", old: true },
    { text: "Maternity Related Concerns/Pregnancy", value: "MaternityPregnancy", old: true },
    { text: "Medical Condition", value: "MedicalCondition", old: true },
    { text: "Mental Illness", value: "MentalIllness", old: true },
    { text: "Menopause", value: "Menopause", old: true },
    { text: "MSD Problem", value: "MSDProblem" },
    { text: "Nervous System Disorders", value: "NervousSystemDisorders", old: true },
    { text: "Neurological", value: "Neurological", old: true },
    { text: "No Issues", value: "NoIssues", old: true },
    { text: "Ophthalmic", value: "Ophthalmic", old: true },
    { text: "Respiratory Conditions", value: "Respiratory", old: true },
    { text: "Short Term Sickness", value: "ShortTermSickness", old: true },
    { text: "Skin Disorders", value: "SkinDisorders", old: true },
    { text: "Spectrum Disorders", value: "SpectrumDisorders", old: true },
    { text: "Stress", value: "Stress", old: true },
    { text: "Substance Misuse", value: "SubstanceMisuse", old: true },
    { text: "Surgical Procedures", value: "SurgicalProcedures", old: true },
    { text: "Verbal Assault – Stress", value: "VerbalAssaultStress", old: true }
];

export interface ClinicalNotesAutoSaveDto {
    id: string;
    data: QuestionnaireData;
    eTag: string;
}

export const iaptQuestionnaireAnswers = [
    { score: 0, text: "Not at all" },
    { score: 1, text: "Several days" },
    { score: 2, text: "More than half the days" },
    { score: 3, text: "Nearly every day" }
];

export const iaptQuestionnaireAnswerOptions: string[] = iaptQuestionnaireAnswers.map(a => a.text);

export const iaptQuestionnaireWsasAnswerOptions: string[] = [
    "0",
    "1",
    "2",
    "3",
    "4",
    "5",
    "6",
    "7",
    "8"
];
